import React from 'react'
import { Box, Typography, InputLabel } from '@mui/material'
import {
  useFormContext,
  Controller,
  FieldValues,
  Path
} from 'react-hook-form'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import DatePicker from '../DatePicker'
import { zoneAbbrs, DATE_STRING } from '../../types/constants'
import InputErrorMessage from '../InputErrorMessage'
import theme from '../theme'

dayjs.extend(timezone)

interface DurationFields extends FieldValues {
  duration: {
    starts_at?: string | null
    ends_at?: string | null
  }
}

interface Props {
  flightStart?: string | null
  flightEnd?: string | null
  context?: 'flight' | 'creative'
  hasEnded?: boolean
  onDateTouchedChange?: (flags: { startTouched?: boolean; endTouched?: boolean }) => void
}

const FlightDates = <T extends DurationFields>({
  flightStart,
  flightEnd,
  context = 'flight',
  hasEnded,
  onDateTouchedChange,
}: Props) => {
  const {
    setValue,
    control,
    formState: { errors }
  } = useFormContext()

  const defaultTimezone = (): { label: string, value: string } => {
    const LAPacific = 'America/Los_Angeles'
    const isDST = dayjs().tz(LAPacific).format('z') !== zoneAbbrs[LAPacific][0]
    const abbr = zoneAbbrs[LAPacific][isDST ? 1 : 0]

    return {
      label: `${LAPacific.replace('America/', '').replace(/_/g, ' ')} (${abbr})`,
      value: LAPacific
    }
  }

  const time_zone = defaultTimezone().value

  const getMaxStartDate = (fieldValue: { ends_at?: string }) => {
    return flightEnd ? dayjs(flightEnd) : dayjs(fieldValue.ends_at)
  }

  const getMinStartDateTime = () => {
    if (flightStart) {
      return dayjs(flightStart)
    }
    return dayjs().tz(time_zone) // For new flights, cannot select past times
  }

  const getMinEndDateTime = (fieldValue: { starts_at?: string }) => {
    if (flightStart) {
      return dayjs(flightStart)
    }

    if (fieldValue.starts_at) {
      const startDateTime = dayjs(fieldValue.starts_at)
      const now = dayjs().tz(time_zone)

      return startDateTime.isAfter(now) ? startDateTime : now
    } else {
      return dayjs().tz(time_zone)
    }
  }

  const getInputLabel = (field: string, text: string) => {
    return (
      <InputLabel
        id={`${field}-label`}
        sx={{
          marginBottom: theme.space['12'],
          color:
            errors && field in errors
              ? theme.palette['red-500']
              : theme.palette['sage-900']
        }}
      >
        <Typography variant="body-11" sx={{ fontWeight: 500 }}>
          {text}
        </Typography>
      </InputLabel>
    )
  }

  const getDefaultDateText = (date: string | null | undefined): string => {
    if (!date) return 'TBD'
    
    const dateObj = dayjs(date).tz(time_zone)
    const now = dayjs().tz(time_zone)
    
    // If the date has passed, show today's date
    if (dateObj.isBefore(now, 'day')) {
      return now.format('MMM D, YYYY')
    }
    
    // Otherwise show the formatted date
    return dateObj.format('MMM D, YYYY')
  }

  return (
    <Box
      sx={{
        borderRadius: theme.borders['rounded-16'],
        padding: context === 'flight' ? theme.space['40'] : 0,
        backgroundColor: theme.palette['base-white'],
        gap: theme.space['16'],
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {context === 'flight' && (
        <Typography variant="heading-4">Flight Dates</Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '21px',
          alignItems: 'start',
        }}
      >
        <Controller
          name={'duration' as Path<T>}
          control={control}
          render={({ field }) => (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: theme.space['20'],
                flex: 1
              }}
            >
              <Box sx={{ flex: 1 }}>
                {getInputLabel('duration.starts_at', 'Start Date')}
                <DatePicker
                  {...field}
                  label=''
                  name="duration.starts_at"
                  value={
                    field.value?.starts_at ? dayjs.tz(field.value.starts_at, time_zone) : null
                  }
                  minDateTime={getMinStartDateTime()}
                  maxDate={getMaxStartDate(field.value)}
                  onChange={(newValue) => {
                    const newDate = newValue ? newValue.format(DATE_STRING) : ''
                    setValue('duration' as Path<T>, {
                      ...field.value,
                      starts_at: newDate
                    })
                    onDateTouchedChange && onDateTouchedChange({ startTouched: true })
                  }}
                  width="100%"
                />
                {context === 'creative' && (
                  <Typography
                    variant="body-11"
                    sx={{
                      fontStyle: 'italic',
                      color: theme.palette['sage-600'],
                      marginTop: theme.space['4'],
                      fontSize: '12px',
                      display: 'block',
                      lineHeight: 1.2
                    }}
                >
                  Optional: defaults to flight start ({getDefaultDateText(flightStart)})
                </Typography>
                )}
                {errors?.duration &&
                  'starts_at' in errors.duration &&
                  errors.duration.starts_at && (
                    <InputErrorMessage
                      error={errors.duration.starts_at.message as string}
                    />
                  )}
              </Box>
              <Box sx={{ flex: 1 }}>
                {getInputLabel('duration.ends_at', 'End Date')}
                <DatePicker
                  {...field}
                  label=''
                  name="duration.ends_at"
                  value={
                    field.value?.ends_at ? dayjs.tz(field.value.ends_at, time_zone) : null
                  }
                  minDateTime={getMinEndDateTime(field.value)}
                  maxDate={flightEnd ? dayjs(flightEnd) : undefined}
                  onChange={(newValue) => {
                    let newDate = ''
                    if (newValue) {
                      const dateToFormat = field.value?.ends_at
                        ? newValue
                        : newValue.endOf('day').subtract(1, 'second')
                      newDate = dateToFormat.format(DATE_STRING)
                    }

                    setValue('duration' as Path<T>, {
                      ...field.value,
                      ends_at: newDate
                    })
                    onDateTouchedChange && onDateTouchedChange({ endTouched: true })
                  }}
                  width="100%"
                  disabled={hasEnded}
                />
                {context === 'creative' && (
                  <Typography
                    variant="body-11"
                    sx={{
                      fontStyle: 'italic',
                      color: theme.palette['sage-600'],
                      marginTop: theme.space['4'],
                      fontSize: '12px',
                      display: 'block',
                      lineHeight: 1.2
                    }}
                  >
                    Optional: defaults to flight end ({getDefaultDateText(flightEnd)})
                  </Typography>
                )}
                {errors?.duration &&
                  'ends_at' in errors.duration &&
                  errors.duration.ends_at && (
                    <InputErrorMessage
                      error={errors.duration.ends_at.message as string}
                    />
                  )}
              </Box>
            </Box>
          )}
        />
      </Box>
    </Box>
  )
}

export default FlightDates
